import { AbstractControl } from '@angular/forms';

// IN RFB nº 2.229/2024: o CNPJ aceita [A-Z0-9] nas 12 primeiras posições a partir
// de julho/2026; os 2 últimos caracteres permanecem dígitos verificadores numéricos.
// O cálculo do DV usa (charCodeAt - 48) para que letras maiúsculas (A=17, …, Z=42)
// e dígitos (0-9) participem da soma ponderada do módulo 11.

const CNPJ_BODY_REGEX = /^[A-Z0-9]{12}\d{2}$/;
const CPF_REGEX = /^\d{11}$/;

function sanitizeCpf(input: string): string {
  return (input || '').replace(/[^0-9]/g, '');
}

function sanitizeCnpj(input: string): string {
  return (input || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function calcCnpjCheckDigit(base: string, length: number): number {
  let soma = 0;
  let pos = length - 7;
  for (let i = length; i >= 1; i--) {
    soma += (base.charCodeAt(length - i) - 48) * pos--;
    if (pos < 2) {
      pos = 9;
    }
  }
  return soma % 11 < 2 ? 0 : 11 - (soma % 11);
}

export function validateCpf(input: string) {
  const value = sanitizeCpf(input);
  if (!value) {
    return false;
  }

  if (!CPF_REGEX.test(value)) {
    return false;
  }

  if (/^(\d)\1{10}$/.test(value)) {
    return false;
  }

  // Valida 1o digito
  let add = 0;
  for (let i = 0; i < 9; i++) {
    add += +value.charAt(i) * (10 - i);
  }

  let rev = 11 - (add % 11);
  if (rev >= 10) {
    rev = 0;
  }

  if (rev !== +value.charAt(9)) {
    return false;
  }

  // Valida 2o digito
  add = 0;
  for (let i = 0; i < 10; i ++) {
    add += +value.charAt(i) * (11 - i);
  }

  rev = 11 - (add % 11);
  if (rev >= 10) {
    rev = 0;
  }

  return rev === +value.charAt(10);
}

export function validateCnpj(input: string) {
  const value = sanitizeCnpj(input);
  if (!value) {
    return false;
  }

  if (!CNPJ_BODY_REGEX.test(value)) {
    return false;
  }

  // Elimina CNPJs com todos os caracteres iguais (ex.: "00000000000000", "AAAAAAAAAAAA00")
  if (/^(.)\1{13}$/.test(value)) {
    return false;
  }

  const dv1Esperado = +value.charAt(12);
  const dv2Esperado = +value.charAt(13);

  const dv1Calculado = calcCnpjCheckDigit(value.substring(0, 12), 12);
  if (dv1Calculado !== dv1Esperado) {
    return false;
  }

  const dv2Calculado = calcCnpjCheckDigit(value.substring(0, 13), 13);
  return dv2Calculado === dv2Esperado;
}

export function cpfValidator(control: AbstractControl) {
  const value = control.value;

  if (!value) {
    return null;
  }

  return validateCpf(value) ? null : { cpf: { value } };
}

export function cnpjValidator(control: AbstractControl) {
  const value = control.value;

  if (!value) {
    return null;
  }

  return validateCnpj(value) ? null : { cnpj: { value } };
}

export function cpfCnpjValidator(control: AbstractControl) {
  const value = control.value;

  if (!value) {
    return null;
  }

  const isValid = validateCpf(value) || validateCnpj(value);
  return isValid ? null : { cpfCnpj: { value } };
}
